import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, ArrowLeft, MessageCircle, Loader2, CheckCheck } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Public buyer-facing inquiry chat. Reached via token URL `/consultas/:token`.
 * Reads/writes through the inquiry token (no login needed).
 */
export default function InquiryChatPage() {
  const { token } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/inquiries/by-token/${token}`);
      setInquiry(res.data.inquiry);
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Consulta no encontrada');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000); // poll every 15s for new producer messages
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const res = await api.post(`/inquiries/by-token/${token}/messages`, { message: body });
      setMessages((prev) => [...prev, res.data.message]);
      setReply('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white/50">
        <Loader2 className="animate-spin mr-2" /> Cargando consulta…
      </div>
    );
  }
  if (!inquiry) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-center text-white/60 px-6">
        <div>
          <MessageCircle size={32} className="mx-auto mb-3 opacity-40" />
          <p>Esta consulta no existe o expiró.</p>
          <Link to="/" className="text-violet-400 hover:underline mt-3 inline-block">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const statusLabel = {
    open: 'Pendiente de respuesta',
    answered: 'Productora respondió',
    sold: 'Reserva confirmada',
    closed: 'Conversación cerrada',
  }[inquiry.status] || inquiry.status;

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      <div className="border-b border-white/10 px-4 sm:px-6 py-3 flex items-center gap-3 bg-zinc-900/60 backdrop-blur sticky top-0 z-10">
        <Link to={`/evento/${inquiry.event_id}`} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/50 truncate">Consulta · {inquiry.tier_name}</p>
          <p className="text-sm font-bold truncate">{inquiry.event_title}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${inquiry.status === 'sold' ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : inquiry.status === 'closed' ? 'border-white/20 text-white/40' : inquiry.status === 'answered' ? 'border-violet-500/40 text-violet-300 bg-violet-500/10' : 'border-amber-500/40 text-amber-300 bg-amber-500/10'}`}>
          {statusLabel}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-2 max-w-3xl w-full mx-auto">
        {messages.map((m) => {
          const fromBuyer = m.sender_type === 'buyer';
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${fromBuyer ? 'justify-end' : 'justify-start'}`}
              data-testid={`inquiry-msg-${m.sender_type}`}
            >
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${fromBuyer ? 'bg-violet-500 text-white' : 'bg-white/5 border border-white/10 text-white'}`}>
                <p className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.sender_name}</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                <p className="text-[10px] opacity-50 mt-1 flex items-center gap-1 justify-end">
                  {new Date(m.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  {fromBuyer && m.read_at && <CheckCheck size={11} className="opacity-80" />}
                </p>
              </div>
            </motion.div>
          );
        })}
        <div ref={endRef} />
      </div>

      {inquiry.status !== 'closed' ? (
        <form onSubmit={send} className="border-t border-white/10 bg-zinc-900/60 backdrop-blur px-4 sm:px-6 py-3 flex items-end gap-2 max-w-3xl w-full mx-auto">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); }
            }}
            placeholder="Escribí tu respuesta…"
            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none resize-none max-h-32"
            data-testid="buyer-reply-input"
          />
          <button
            type="submit"
            disabled={sending || !reply.trim()}
            className="p-2.5 rounded-full bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-40"
            data-testid="buyer-reply-send"
          >
            <Send size={16} />
          </button>
        </form>
      ) : (
        <div className="border-t border-white/10 px-4 py-4 text-center text-xs text-white/40 max-w-3xl w-full mx-auto">
          Esta conversación fue cerrada por la productora.
        </div>
      )}
    </div>
  );
}
